/**
 * @snw/protocol — wire types for the SayNoWay room protocol.
 *
 * Rules of the protocol (see docs/PROTOCOL.md):
 *  - Every client command carries a client-generated `cmdId`; the server treats a repeated
 *    cmdId from the same principal as the same command and replays the stored ack.
 *  - Secrets (the target's locked step, predictors' guesses) never appear in any event whose
 *    `to` is 'all' before the corresponding reveal event. They only ever appear in the sender's
 *    own private ack/event, and in `round.revealed` after the staged reveal has finished.
 *  - All deadlines are server epoch milliseconds. Clients never decide phase changes.
 */
import { z } from 'zod';
import { Gender } from './profile.js';
export const PROTOCOL_VERSION = 1;
export const RoomType = z.enum(['friends', 'meet', 'flirt']);
export const Funding = z.enum(['credits', 'ad']);
/**
 * The room's ladder from safe to spicy (owner 5.10.2026), replacing the old five-word vibe that mixed a boldness, a
 * seriousness and a topic into one control and told nobody anything.
 *
 *   friendly  the room's OWN deck, no K-18     — a Friends room's default
 *   dating    the room's OWN deck, no K-18     — a Date room's default, the same step under its own name
 *   bolder    the room's OWN deck + K-18       — "Bolder (Date + K-18)" in a Date room, Friends + K-18 in a Friends room
 *   flirty    the FLIRT deck, no K-18
 *   naughty   the FLIRT deck + K-18
 *
 * One control picks BOTH the deck and whether K-18 cards join the draw, which is why there is no separate K-18
 * switch. Friends and Date rooms offer it — four steps each, differing only in what the first one is called. Meet
 * and Flirt tables do not: the house sets what a public table plays (owner's choice, 5.10.2026).
 * bolder and naughty are open only to a player who has stated they are an adult.
 */
export const Vibe = z.enum(['friendly', 'dating', 'bolder', 'flirty', 'naughty']);
/** the two steps that draw K-18 cards and therefore need an adult */
export const VIBE_ADULT = ['bolder', 'naughty'];
/** what a room offers, in order; the first is its default */
export const VIBES_FOR = (dateMode) => (dateMode ? ['dating', 'bolder', 'flirty', 'naughty'] : ['friendly', 'bolder', 'flirty', 'naughty']);
/** Participation conditions (spec §7) — separate from purpose (roomType) and visibility */
/** who can join a listed table: anyone · anyone who has stated a gender · women · men · MIX. MIX (owner 6.10.2026): at least a
 *  third of the table is the other side — two of one side may sit before the first of the other (the host and a friend), after
 *  that a side may be at most twice the other; never one woman with five men. Rooms stored before 6.10.2026 may say 'mixed_3_3'. */
export const Eligibility = z.enum(['open', 'all_genders', 'women', 'men', 'mixed']);
export const AgeBand = z.enum(['any', '18_24', '25_34', '35_44', '45plus']);
/** Readable self-declared identity, including stored legacy answers. New profile writes use Gender. */
export const Identity = z.enum([...Gender.options, 'nonbinary', 'unspecified']);
/** INTRO (owner 7.10.2026): the target's player card is shown to the table for a fixed five seconds BEFORE the predict window; it never
 *  eats predict time, repeats for every target turn, and only the server's clock ends it. */
export const Phase = z.enum(['LOBBY', 'SPONSOR_BREAK', 'LINE_LOCK', 'INTRO', 'PREDICT', 'REVEAL', 'SCORE', 'ROUND_END', 'MATCH_END']);
export const INTRO_MS = 5000;
/** 1..6 = first refusal step; 7 = accepted all six ("All 6"). */
export const LineStep = z.number().int().min(1).max(7);
/** 00-CODEX-UI-HANDOFF.md §8: at most 8 characters — the roster pills, the winner line and the table hold that */
export const DisplayName = z.string().trim().min(1).max(8);
export const AvatarId = z.string().regex(/^[a-z0-9_-]{1,40}$/);
export const RoomCode = z.string().regex(/^[A-Z0-9]{6}$/);
export const CmdId = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/);
export const Sponsor = z.object({
    campaignId: z.string(),
    brandName: z.string().max(60),
    disclosure: z.string().max(120),
    creativeUrl: z.string().url().optional(),
    ctaUrl: z.string().url().optional(),
});
/** A Line = one hypothetical with six monotonically escalating steps. */
export const LineContent = z.object({
    id: z.string(),
    roomType: RoomType,
    theme: z.string(),
    prompt: z.string().max(140), // "Give up your phone…"
    question: z.string().max(140), // "…for how long?" (shown above the ladder)
    steps: z.array(z.string().max(60)).length(6),
    escalationAxis: z.string(),
    sponsor: Sponsor.optional(),
});
export const RoomSettings = z.object({
    roomType: RoomType,
    funding: Funding,
    maxPlayers: z.number().int().min(2).max(8),
    /** OD-014: match length = shared Lines that EVERY player answers. Friends/Meet/Flirt 1-3 (default 3), Friends+Date 1-6 (default 6). */
    linesPerMatch: z.number().int().min(1).max(6),
    /** Friends submode: exactly two players, Date-eligible content, up to 6 Lines. Not the same as 18+ content. */
    dateMode: z.boolean().optional(),
    lockSeconds: z.number().int().min(10).max(120),
    predictSeconds: z.number().int().min(10).max(120),
    /** the target's card intro before each predict window, in ms; absent = INTRO_MS. The server stamps it from its own switch
     *  (0 while the card is not yet shipped), so the room never pauses for a card that is not there. Not patchable in a room. */
    introMs: z.number().int().min(0).max(15000).optional(),
    revealStepMs: z.number().int().min(300).max(4000),
    language: z.string().min(2).max(8),
    vibe: Vibe.optional(),
    /** private = only the host's friends can enter (with the link); link = anyone with the link; listed = shown in the public list (owner gate) */
    visibility: z.enum(['private', 'link', 'listed']),
    eligibility: Eligibility.optional(),
    ageBand: AgeBand.optional(),
    /** public play (OD-012): the house hosts the room (no human host), region key ('ww' or ISO country), sponsored option → campaign */
    house: z.boolean().optional(),
    region: z.string().min(2).max(8).optional(),
    sponsorCampaignId: z.string().max(64).optional(),
});
// ---------------------------------------------------------------- commands (client -> server)
const base = { cmdId: CmdId };
export const ClientCommand = z.discriminatedUnion('type', [
    z.object({ ...base, type: z.literal('room.ready'), ready: z.boolean() }),
    // host, lobby / match end only: change what the next match plays (rounds, pace, funding, vibe, seats)
    z.object({ ...base, type: z.literal('room.settings'), patch: z.object({ linesPerMatch: z.number().int().min(1).max(6).optional(), lockSeconds: z.number().int().min(10).max(120).optional(), predictSeconds: z.number().int().min(10).max(120).optional(), revealStepMs: z.number().int().min(300).max(4000).optional(), funding: Funding.optional(), vibe: Vibe.optional(), maxPlayers: z.number().int().min(2).max(8).optional(), visibility: z.enum(['private', 'link', 'listed']).optional(), eligibility: Eligibility.optional(), ageBand: AgeBand.optional() }) }),
    // host, lobby / match end only: remove a player from the room
    z.object({ ...base, type: z.literal('room.kick'), playerId: z.string().min(1).max(64) }),
    z.object({ ...base, type: z.literal('match.start') }),
    z.object({ ...base, type: z.literal('line.lock'), lineId: z.string(), step: LineStep }),
    z.object({ ...base, type: z.literal('round.guess'), roundId: z.string(), step: LineStep, call: z.boolean() }),
    z.object({ ...base, type: z.literal('line.pass'), lineId: z.string() }),
    z.object({ ...base, type: z.literal('round.next') }),
    z.object({ ...base, type: z.literal('match.rematch') }),
    // 34-rematch-join / 34-rematch-ready: propose a rematch after a match, answer IN / OUT (also NOT THIS TIME and LEAVE REMATCH), sponsor the whole crew (Friends)
    z.object({ ...base, type: z.literal('rematch.propose') }),
    z.object({ ...base, type: z.literal('rematch.answer'), in: z.boolean() }),
    z.object({ ...base, type: z.literal('rematch.sponsor') }),
    // 34-match-end.md §3 MESSAGE CREW: a short line to the crew of the finished match; delivered as an event, never stored
    z.object({ ...base, type: z.literal('crew.say'), text: z.string().trim().min(1).max(80) }),
    z.object({ ...base, type: z.literal('sponsor.ack') }),
    z.object({ ...base, type: z.literal('room.leave') }),
]);
export const ClientFrame = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('hello'), protocolVersion: z.number().int(), clientBuild: z.string().max(40), lastSeq: z.number().int().optional() }),
    z.object({ kind: z.literal('cmd'), cmd: ClientCommand }),
    z.object({ kind: z.literal('ping'), t: z.number() }),
]);
export const PRIVATE_EVENT_TYPES = new Set(['own.locked', 'own.guess']);
export const CLOSE_CODES = {
    AUTH_FAILED: 4001,
    RECONNECT_FAILED: 4003,
    PROTOCOL_MISMATCH: 4400,
    KICKED: 4409,
    RATE_LIMITED: 4429,
    SERVER_SHUTDOWN: 4500,
};
/**
 * OD-014 + the owner's Date directive (9.9.2026): Date is a Friends submode played by exactly two people over 1-6 Lines
 * (default 6); every other mode plays 1-3 (default 3). `room.settings` enforces this once a room exists — this is the
 * same invariant at the door, so a room can never be *created* in a state the engine would refuse. A caller that asks
 * for Date without saying how many seats or Lines gets the mode's own defaults; a caller that asks for a contradiction
 * is refused rather than silently rewritten.
 */
/**
 * What a match costs its host, in credits (owner 12.9.2026).
 *
 * A private room is the expensive one because it is the whole evening: the host buys the table for two to six people
 * they chose. A public table is one seat at someone else's table, and it is the cheap way in. The welcome grant is
 * three, which is exactly one public game plus one private one — that is the arithmetic the owner set the prices by,
 * so the two numbers are written here together rather than in two places that can drift apart.
 */
export function matchPrice(s) {
    return s.dateMode || s.roomType === 'friends' ? 2 : 1;
}
/**
 * The welcome grant a new player receives, in credits.
 *
 * Prio1 Pay owns the amount and grants it — a client that names its own grant could grant itself any amount — so this
 * is never sent anywhere. It is here because the prices were set by arithmetic against it: one public seat (1) plus one
 * private room (2). Verified against production on 12.9.2026 with a brand-new account: balance 3, authoritative, and
 * Home shows 3. Pay raised it from 2 the same morning, after Marko confirmed it to them directly (OD-022 updated).
 *
 * Players who signed up before that keep the 2 they were granted; Pay's repeat path returns their own lot unchanged.
 * So this is what a NEW player gets, not what every player has — the client reads real balances from the wallet.
 */
export const WELCOME_CREDITS = 3;
export function settingsRefusal(s) {
    const cap = s.dateMode ? 6 : 3;
    if (s.dateMode && s.roomType !== 'friends')
        return 'DATE_IS_FRIENDS_ONLY';
    if (s.dateMode && s.maxPlayers !== 2)
        return 'DATE_IS_FOR_TWO';
    if (s.linesPerMatch < 1 || s.linesPerMatch > cap)
        return 'BAD_LINES';
    /* There is deliberately no rule against a room holding the other room's first step. 'friendly' and 'dating' are
       the SAME step under two names — drawFor() cannot tell them apart — so refusing one in the other's room would
       reject something harmless and break every caller that spreads DEFAULT_SETTINGS and flips dateMode. The name is
       chosen for the room by withModeDefaults; what the room PLAYS never depends on it. */
    return null;
}
/** Fill a partial room request with the defaults of the mode it asked for, before it is checked. */
export function withModeDefaults(overrides) {
    const dateMode = overrides.dateMode ?? DEFAULT_SETTINGS.dateMode ?? false;
    // a Date room's first step is called Dating, not Friendly (owner 5.10.2026) — the same step, its own name
    const modeDefaults = dateMode ? { maxPlayers: 2, linesPerMatch: 6, vibe: 'dating' } : {};
    return { ...DEFAULT_SETTINGS, ...modeDefaults, ...overrides };
}
export const DEFAULT_SETTINGS = {
    roomType: 'friends',
    vibe: 'friendly',
    /* Friends and Date are paid by one player's credits, Meet and Flirt by each joiner's — unless a company's
       campaign carries the table ('ad'), which only house tables can have. */
    funding: 'credits',
    maxPlayers: 6,
    linesPerMatch: 3,
    // 25-lock-chosen.md §10 / 28-predict-call.md §9 / 30-reveal-live.md §7.2 (owner, 14.9.2026): own answer 20 s, prediction
    // 10 s, one reveal step about 0.8 s. The old 45/40/1400 were the pre-anchor guesses.
    lockSeconds: 20,
    predictSeconds: 10,
    revealStepMs: 800,
    language: 'en',
    visibility: 'link',
};
export * from './profile.js';
export * from './f6.js';
export * from './card.js';
export * from './countries.js';
//# sourceMappingURL=index.js.map